import React, { memo, useCallback, useEffect, useState } from "react";

import {
  Box,
  Text,
  Spinner,
  VStack,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  HStack,
  Checkbox,
  Link,
  Button,
} from "@chakra-ui/react";
import colors from "app/colors";
import SyncoLogoIcon from "modules/icons/synco-logo-icon";
import { FieldValues, useForm } from "react-hook-form";
import {
  formatPhoneNumber,
  getMessageFromError,
} from "modules/utils/Functions";
import { AUTH_PATH, SERVER_ERROR } from "modules/utils/Constants";
import isEmail from "validator/lib/isEmail";
import isMobilePhone from "validator/lib/isMobilePhone";
import { useRouter } from "next/router";
import { SUCCESS_QUERY_STRING } from "modules/hooks/use-query-string";
import { InfoIcon } from "modules/icons";
import { API_INVITE_PATH, Invite } from "./use-invite";
import { NEXT_PUBLIC_API_BASE_URL } from "app/client";

interface IProps {
  invite: Invite;
}

interface ConsumeInviteInput {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  isSms: boolean;
}

interface ConsumeInviteResponse {
  success: boolean;
  message: string;
  errorType?: string;
}

interface ServerError {
  message: string;
  errorType?: string;
}

const INVITE_USER_EXISTS_ERROR_TYPE = "INVITE_USER_EXISTS";

const InviteForm = ({ invite }: IProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { replace, query } = useRouter();
  const [serverError, setServerError] = useState<ServerError | null>(null);

  const consumeInviteAPI = useCallback(
    async (input: ConsumeInviteInput): Promise<ConsumeInviteResponse> => {
      const response = await fetch(
        `${NEXT_PUBLIC_API_BASE_URL}${API_INVITE_PATH}/consume`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(input),
        },
      );
      return response.json();
    },
    [],
  );

  const defaultFirstName = invite?.firstName ?? "";
  const defaultLastName = invite?.lastName ?? "";
  const defaultEmail = invite?.email ?? "";
  const defaultPhone = formatPhoneNumber(invite?.phone) ?? "";

  const {
    handleSubmit,
    formState: { errors, isValid },
    register,
    clearErrors,
    setError,
    reset,
    trigger,
    watch,
  } = useForm<FieldValues>({
    defaultValues: {
      firstName: defaultFirstName,
      lastName: defaultLastName,
      email: defaultEmail,
      phone: defaultPhone,
      isSms: true,
    },
    mode: "onChange",
  });

  const email = watch("email");
  const phone = watch("phone");

  useEffect(() => {
    reset({
      firstName: defaultFirstName,
      lastName: defaultLastName,
      email: defaultEmail,
      phone: defaultPhone,
      isSms: true,
    });
    trigger();
  }, [
    defaultEmail,
    defaultFirstName,
    defaultLastName,
    defaultPhone,
    reset,
    trigger,
  ]);

  useEffect(() => {
    trigger(["email", "phone"]);
  }, [email, phone, trigger]);

  const onSubmit = useCallback(
    async ({ firstName, lastName, email, phone, isSms }: FieldValues) => {
      setIsSubmitting(true);
      setServerError(null);
      try {
        const { success, message, errorType } = await consumeInviteAPI({
          id: invite.id,
          firstName,
          lastName,
          email: email || undefined,
          phone: phone ? formatPhoneNumber(phone) : undefined,
          isSms: !!phone && (isSms as boolean),
        });
        if (!success) {
          setServerError({
            message: message || "An unexpected error occurred",
            errorType,
          });
          setError("email", {
            type: "value",
          });
          setError("phone", {
            type: "value",
          });
          return;
        }
        replace({
          query: {
            ...query,
            [SUCCESS_QUERY_STRING]: true,
          },
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "An unexpected error occurred";
        setServerError({
          message: errorMessage || "An unexpected error occurred",
        });
        setError("email", {
          type: "value",
        });
        setError("phone", {
          type: "value",
        });
        return;
      } finally {
        setIsSubmitting(false);
      }
    },
    [consumeInviteAPI, invite.id, query, replace, setError],
  );

  useEffect(() => {
    if (!invite) {
      return;
    }
    if (invite.type === "USER" && invite.userIdList.length > 0) {
      replace({
        pathname: AUTH_PATH,
      });
    }
  }, [invite, replace]);

  if (!invite) {
    return <Spinner />;
  }

  return (
    <VStack
      bg={colors.white}
      borderRadius={32}
      w="100%"
      maxW="444px"
      spacing={4}
      py={4}
      px={8}
      m={2}
    >
      <Box>
        <SyncoLogoIcon boxSize={110} />
      </Box>
      <VStack spacing={0}>
        <Text
          fontFamily="Outfit"
          fontSize={28}
          fontWeight={700}
          lineHeight={8}
          textAlign="center"
        >
          {`Join ${invite.organizationName} on Synco`}
        </Text>
        <Text color={colors.grayMain} fontSize={16} fontWeight={500}>
          Add your info to get started.
        </Text>
      </VStack>
      <form
        id="invite-form"
        onSubmit={handleSubmit(onSubmit)}
        style={{ width: "100%" }}
      >
        <VStack w="full" alignItems="flex-start" spacing={4}>
          {serverError && (
            <HStack bg={colors.red20} alignItems="flex-start" px={2} py={1}>
              <InfoIcon boxSize={6} fill={colors.red} />
              <Text
                fontSize={14}
                color={colors.red}
                fontWeight={500}
                lineHeight="18px"
              >
                {serverError.message || "An unexpected error occurred"}
                {serverError.errorType === INVITE_USER_EXISTS_ERROR_TYPE && (
                  <Link href={AUTH_PATH} color={colors.blue} fontWeight={700}>
                    {" "}
                    Log in here.
                  </Link>
                )}
              </Text>
            </HStack>
          )}
          <FormControl isInvalid={!!errors?.firstName}>
            <VStack alignItems="flex-start" w="full" spacing={0}>
              <FormLabel
                color={colors.gray50}
                fontWeight={400}
                fontSize={14}
                m={0}
              >
                First Name
              </FormLabel>
              <Input
                id="firstName"
                {...register("firstName", {
                  required: {
                    message: "First name is required",
                    value: true,
                  },
                  maxLength: {
                    value: 60,
                    message: "First name must be 60 characters or less",
                  },
                })}
              />
              <FormErrorMessage mt={1}>
                {getMessageFromError(errors?.firstName?.message)}
              </FormErrorMessage>
            </VStack>
          </FormControl>
          <FormControl isInvalid={!!errors?.lastName}>
            <VStack alignItems="flex-start" w="full" spacing={0}>
              <FormLabel
                color={colors.gray50}
                fontWeight={400}
                fontSize={14}
                m={0}
              >
                Last Name
              </FormLabel>
              <Input
                id="lastName"
                {...register("lastName", {
                  required: {
                    message: "Last name is required",
                    value: true,
                  },
                  maxLength: {
                    value: 60,
                    message: "Last name must be 60 characters or less",
                  },
                })}
              />
              <FormErrorMessage mt={1}>
                {getMessageFromError(errors?.lastName?.message)}
              </FormErrorMessage>
            </VStack>
          </FormControl>
          <FormControl isInvalid={!!errors?.phone}>
            <VStack alignItems="flex-start" w="full" spacing={0}>
              <FormLabel
                color={colors.gray50}
                fontWeight={400}
                fontSize={14}
                m={0}
              >
                Mobile Number
              </FormLabel>
              <Input
                id="phone"
                onFocus={() => {
                  clearErrors(["email", "phone", SERVER_ERROR]);
                }}
                {...register("phone", {
                  validate: () => {
                    if (!email && !phone) {
                      return "Either phone or email is required";
                    }
                    return (
                      !phone || isMobilePhone(phone) || "Invalid phone number"
                    );
                  },
                })}
              />
              <FormErrorMessage mt={1}>
                {getMessageFromError(errors?.phone?.message)}
              </FormErrorMessage>
            </VStack>
          </FormControl>
          <FormControl isInvalid={!!errors?.email}>
            <VStack alignItems="flex-start" w="full" spacing={0}>
              <FormLabel
                color={colors.gray50}
                fontWeight={400}
                fontSize={14}
                m={0}
              >
                Email Address
              </FormLabel>
              <Input
                id="email"
                onFocus={() => {
                  clearErrors(["email", "phone", SERVER_ERROR]);
                }}
                {...register("email", {
                  validate: () => {
                    if (!email && !phone) {
                      return "Either phone or email is required";
                    }
                    return !email || isEmail(email) || "Invalid email address";
                  },
                })}
              />
              <FormErrorMessage mt={1}>
                {getMessageFromError(errors?.email?.message)}
              </FormErrorMessage>
            </VStack>
          </FormControl>
          <FormControl hidden={!phone}>
            <HStack w="full">
              <Checkbox id="isSms" defaultChecked {...register("isSms")}>
                <Text color={colors.gray50} fontWeight={400} fontSize={14}>
                  I agree to receive messages via SMS from Synco
                </Text>
              </Checkbox>
            </HStack>
          </FormControl>
          <Text fontSize={12} color={colors.grayMain}>
            By clicking “Continue” you agree to our{" "}
            <Link
              href="https://teamsynco.com/terms-of-service"
              isExternal
              fontWeight={700}
              textDecoration="underline"
            >
              Terms of Use
            </Link>{" "}
            and{" "}
            <Link
              href="https://teamsynco.com/privacy"
              isExternal
              fontWeight={700}
              textDecoration="underline"
            >
              Privacy Policy
            </Link>
          </Text>
          <Button
            h="40px"
            w="80%"
            type="submit"
            isDisabled={!isValid || isSubmitting}
            alignSelf="center"
            onClick={() => {
              clearErrors([SERVER_ERROR]);
            }}
            fontWeight={500}
            fontSize={16}
          >
            {isSubmitting ? <Spinner /> : "Continue"}
          </Button>
        </VStack>
      </form>
    </VStack>
  );
};

export default memo(InviteForm);
